import React, { useEffect, useLayoutEffect, useRef } from "react";
import { Box } from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import { waitForPaint } from "../../utils/waitForPaint";
import { isStrategyRdPathname } from "@/types/strategyRd";
import { isWorkspaceCanvasPathname } from "@/views/Dashboard/Workspace/workspaceRoutes";

interface AppPanelRouteHostProps {
  children: React.ReactNode;
  /**
   * Duplicate/decorative shells (e.g. blurred preview behind billing fullscreen) must not
   * publish `data-app-route-view-ready` or the route loader can dismiss before the real panel paints.
   */
  publishRouteReady?: boolean;
}

export default function AppPanelRouteHost({ children, publishRouteReady = true }: AppPanelRouteHostProps) {
  const location = useLocation();
  const hostRef = useRef<HTMLDivElement>(null);
  const lockViewport =
    isWorkspaceCanvasPathname(location.pathname) || isStrategyRdPathname(location.pathname);

  useLayoutEffect(() => {
    if (!publishRouteReady) return;
    hostRef.current?.removeAttribute("data-app-route-view-ready");
  }, [publishRouteReady, location.pathname, location.search]);

  useEffect(() => {
    if (!publishRouteReady) {
      return undefined;
    }

    const host = hostRef.current;
    if (!host) {
      return undefined;
    }

    let cancelled = false;

    void waitForPaint().then(() => {
      if (!cancelled) {
        host.setAttribute("data-app-route-view-ready", "true");
      }
    });

    return () => {
      cancelled = true;
      host.removeAttribute("data-app-route-view-ready");
    };
  }, [publishRouteReady, location.pathname, location.search]);

  return (
    <Box
      ref={hostRef}
      data-app-panel-route-host
      w="100%"
      minH="0"
      {...(lockViewport
        ? {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            height: "100%",
          }
        : null)}
    >
      {children}
    </Box>
  );
}
